import type { MockServer } from './server.js'
import type { ModelDefinition } from 'miragejs/-types'
import { Model, Factory, Response, belongsTo, hasMany } from 'miragejs'
import type { InvoiceRowData } from '@/composables/api/Invoice.js'
import {
  rand,
  randNumber,
  randFloat,
  randChanceBoolean,
  randBoolean,
  randRecentDate,
  randMask,
} from '@ngneat/falso'
import { getExistingEntity } from './utils.js'
import {
  InvoiceRowUsageSize,
  InvoiceRowUsageType,
  InvoiceRowItemType,
} from '@/models/invoiceRow.js'
import { roundEven, toNumber } from '@/utils/number.js'

export const model = Model.extend({
  invoice: belongsTo('invoice'),
  originalRow: belongsTo('invoiceRow'),
  patient: belongsTo('patient'),
  stockLocations: hasMany('stockLocation'),
  batch: belongsTo('batch'),
  reportingDimension1: belongsTo('listItem'), // Call Center
  reportingDimension2: belongsTo('listItem'), // Project Code
  supervisingVeterinarian: belongsTo('user'),
}) as ModelDefinition<InvoiceRowData>

export const factory = Factory.extend<InvoiceRowData>({
  created() {
    return randRecentDate().toISOString()
  },
  name() {
    return rand([
      'Consultation',
      'Amputation',
      'Supply',
      'Food',
      'Lab Analysis',
      'Medicine',
    ])
  },
  print_name() {
    return rand([
      'Consultation',
      'Amputation',
      'Supply',
      'Food',
      'Lab Analysis',
      'Medicine',
    ])
  },
  item_type() {
    return rand(Object.values(InvoiceRowItemType))
  },
  use_dispense_fee() {
    // Only medicines can have dispense fee
    // 50% chance for medicine items to use dispense fee
    if (this.item_type === InvoiceRowItemType.Medicine) {
      return randChanceBoolean({ chanceTrue: 0.5 })
    }
    return false
  },
  reason_for_credit() {
    return ''
  },
  account_number() {
    return randMask('#########').toString()
  },
  usage_size() {
    return rand(Object.values(InvoiceRowUsageSize))
  },
  usage_type() {
    return rand(Object.values(InvoiceRowUsageType))
  },
  dosage_units() {
    return randNumber({ min: 1, max: 12 })
  },
  quantity() {
    return randNumber({ min: 1, max: 10 })
  },
  units() {
    return roundEven(toNumber(this.quantity) * toNumber(this.dosage_units), 0)
  },
  amount_description() {
    return rand([
      '',
      `${randNumber({ min: 10, max: 50 })}ml`,
      `> ${randNumber({ min: 1, max: 10 })}kg`,
      `> ${randNumber({ min: 500, max: 1000 })}gr`,
    ])
  },
  primary_display_price() {
    return roundEven(
      toNumber(randFloat({ min: 10, max: 100, precision: 4 })),
      4,
    )
  },
  price() {
    return roundEven(
      toNumber(randFloat({ min: 10, max: 100, precision: 4 })),
      4,
    )
  },
  price_with_vat() {
    return roundEven(
      toNumber(randFloat({ min: 10, max: 120, precision: 4 })),
      4,
    )
  },
  percentage_change() {
    // 10% of the time, return a specific value from [20, 30, 40, 50]
    if (randChanceBoolean({ chanceTrue: 0.1 })) {
      const specificValues = [20, 30, 40, 50]
      return rand(specificValues)
    }

    return
  },
  is_additional_tax() {
    return randChanceBoolean({ chanceTrue: 0.3 })
  },
  prescription() {
    return randBoolean()
  },
  vat_percentage() {
    return rand([10, 16, 21, 23])
  },
  remove_vat() {
    return randBoolean()
  },
  // Calculations for this value is done in afterCreate
  display_sum_subtotal() {
    return randNumber({ min: 2, max: 10 })
  },
  // Calculations for this value is done in afterCreate
  display_sum_vat() {
    return randNumber({ min: 2, max: 10 })
  },
  sum_with_vat() {
    // Calculations for this value is done in afterCreate
    return randNumber({ min: 2, max: 10 })
  },
  sum_without_vat() {
    // Calculations for this value is done in afterCreate
    return randNumber({ min: 2, max: 10 })
  },
  sum_vat() {
    // Calculations for this value is done in afterCreate
    return randNumber({ min: 2, max: 10 })
  },

  afterCreate(invoiceRow, server) {
    // Randomly set usage_size to 'USAGE_SIZE_DOSAGE_UNITS' or 'USAGE_SIZE_PACKAGE'
    // Doing this because the randomization above is not working.
    invoiceRow.usage_size = randBoolean()
      ? InvoiceRowUsageSize.Unit
      : InvoiceRowUsageSize.Package

    // Calculate sum_with_vat:
    const quantity =
      invoiceRow.usage_size === InvoiceRowUsageSize.Unit
        ? toNumber(invoiceRow.units)
        : toNumber(invoiceRow.quantity)
    invoiceRow.sum_with_vat = roundEven(
      toNumber(invoiceRow.primary_display_price) * quantity,
      4,
    )

    // Calculate sum_without_vat:
    const vatRate = toNumber(invoiceRow.vat_percentage) / 100
    invoiceRow.sum_without_vat = roundEven(
      invoiceRow.sum_with_vat / (1 + vatRate),
      4,
    )
    invoiceRow.display_sum_subtotal = invoiceRow.sum_without_vat

    // Calculate sum_vat:
    invoiceRow.sum_vat = roundEven(
      invoiceRow.sum_with_vat - invoiceRow.sum_without_vat,
      4,
    )

    invoiceRow.display_sum_vat = invoiceRow.sum_vat

    // Randomly seed patient - 60% chance
    if (randChanceBoolean({ chanceTrue: 0.6 })) {
      invoiceRow.patient = getExistingEntity(server, 'patient')
    }

    // Randomly seed batch - 40% chance
    if (randChanceBoolean({ chanceTrue: 0.4 })) {
      invoiceRow.batch = getExistingEntity(server, 'batch')
    }

    // Randomly seed stockLocations - 30% chance
    if (randChanceBoolean({ chanceTrue: 0.3 })) {
      const stockLocation = getExistingEntity(server, 'stockLocation')
      invoiceRow.update({ stockLocations: [stockLocation] })
    }

    // Randomly seed reportingDimension1 (Call Center) - 40% chance
    if (randChanceBoolean({ chanceTrue: 0.4 })) {
      invoiceRow.reportingDimension1 = getExistingEntity(server, 'listItem')
    }

    // Randomly seed reportingDimension2 (Project Code) - 30% chance
    if (randChanceBoolean({ chanceTrue: 0.3 })) {
      invoiceRow.reportingDimension2 = getExistingEntity(server, 'listItem')
    }

    // Randomly seed supervisingVeterinarian - 50% chance
    if (randChanceBoolean({ chanceTrue: 0.5 })) {
      invoiceRow.supervisingVeterinarian = getExistingEntity(server, 'user')
    }
  },
})

export function routes(server: MockServer) {
  server.resource('invoiceRow', { path: '/invoice_rows' })

  server.patch(
    '/invoice_rows/:id/supervising_veterinarian',
    function (schema, request) {
      const id = request.params.id
      const invoiceRow = schema.find('invoiceRow', id)
      const requestBody = JSON.parse(request.requestBody)

      if (!invoiceRow) {
        return new Response(
          404,
          {},
          {
            errors: [{ detail: `InvoiceRow with id ${id} not found` }],
          },
        )
      }

      const vetData =
        requestBody.data?.relationships?.supervising_veterinarian?.data
      const vet = vetData ? schema.find('user', vetData.id) : null

      invoiceRow.update({ supervisingVeterinarian: vet })

      return invoiceRow
    },
  )
}
